import Skeleton from '@material-ui/lab/Skeleton'
import React, { useState } from 'react'
import {
  Dialog,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  DialogTitle,
  IconButton,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import { useDialogs, useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import ClearIcon from '@material-ui/icons/Clear'
import CheckIcon from '@material-ui/icons/Done'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { Sentry } from 'src/util/appSentry'
import { useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useHistory } from 'react-router-dom'
import { triggerFlow, getChargebeeProductPrice } from '@paintscout/util'
import { useSubscriptionSummaryQuery } from '@paintscout/api'
import { track } from 'shared/util/analytics'
import classNames from 'classnames'

const BACKGROUND_IMAGE_URL =
  'https://paintscout.s3.us-east-1.amazonaws.com/product-marketing/Operations%2BAnnouncement.webp'
const MOBILE_BACKGROUND_IMAGE_URL =
  'https://paintscout.s3.us-east-1.amazonaws.com/product-marketing/Operations%2BAnnoucement%2BMobile.webp'

const useStyles = makeStyles<Theme, DialogProps & { isPromptPage?: boolean }>(
  (theme) => ({
    dialogRoot: {
      zIndex: ({ isPromptPage }) => (isPromptPage ? '1 !important' : undefined)
    },
    dialogPaper: {
      overflowX: 'hidden',
      border: 'none'
    },
    promptPagePaper: {
      marginTop: theme.spacing(12),
      [theme.breakpoints.down(600)]: {
        paddingTop: theme.spacing(6),
        marginTop: 0
      }
    },
    dialogTitle: {
      padding: theme.spacing(2, 3),
      display: 'flex',
      alignItems: 'center'
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      padding: 0,
      zIndex: 3000,
      color: 'white',
      [theme.breakpoints.down(600)]: {
        top: theme.spacing(3),
        right: theme.spacing(3)
      }
    },
    dialogContent: {
      backgroundImage: `url(${BACKGROUND_IMAGE_URL})`,
      backgroundSize: 'cover',
      padding: theme.spacing(3, 4),
      color: 'white',
      minHeight: 220,
      [theme.breakpoints.down('xs')]: {
        backgroundPosition: 'center center'
      },
      [theme.breakpoints.down(600)]: {
        backgroundImage: `url(${MOBILE_BACKGROUND_IMAGE_URL})`,
        flex: 1,
        overflowY: 'auto',
        backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
        borderRadius: `${theme.borderRadius.lg} ${theme.borderRadius.lg} 0px 0px`,
        padding: theme.spacing(2) + 'px !important',
        backgroundPosition: 'bottom bottom'
      }
    },
    dialogContentBreakdown: {
      padding: theme.spacing(3)
    },
    primaryColor: {
      color: theme.paintscout.palette.brand.Blue.Blue80
    },
    title: {
      fontFamily: 'euclid',
      fontSize: theme.typography.pxToRem(40),
      color: 'white',
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down(600)]: {
        fontSize: theme.typography.pxToRem(30)
      }
    },
    subtitle: {
      marginTop: theme.spacing(0.5),
      color: 'white',
      [theme.breakpoints.down(600)]: {
        fontSize: theme.typography.pxToRem(14),
        color: theme.paintscout.palette.brand.Neutral.P80,
        fontWeight: 500
      }
    },
    mainContent: {
      marginTop: theme.spacing(2),
      [theme.breakpoints.down(600)]: {
        marginTop: theme.spacing(1)
      }
    },
    listItem: {
      padding: 0,
      paddingBottom: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'flex-start',
      [theme.breakpoints.down(600)]: {
        paddingBottom: 0
      }
    },
    listItemIcon: {
      minWidth: '16px',
      maxWidth: '16px',
      marginTop: theme.spacing(1)
    },
    checkIcon: {
      color: theme.paintscout.palette.brand.Blue.Blue80,
      fontSize: theme.typography.pxToRem(16)
    },
    listItemText: {
      marginLeft: theme.spacing(1),
      '& .MuiListItemText-primary': {
        color: 'white'
      },
      [theme.breakpoints.down(600)]: {
        '& span': {
          fontSize: theme.typography.pxToRem(14)
        }
      }
    },
    pricing: {
      marginTop: theme.spacing(3)
    },
    pricingLine1: {
      fontSize: '1.2rem',
      color: 'white',
      fontFamily: 'euclid',
      fontWeight: 600
    },
    pricingLine2: {
      color: 'white',
      fontFamily: 'euclid'
    },
    bottomMessage: {
      width: '100%'
    },
    mobileWrapper: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    },
    mobileImageWrapper: {
      display: 'flex',
      border: '16px solid white',
      height: '100%',
      borderBottom: 'none',
      borderRadius: 10
    },
    mobileFooter: {
      flexShrink: 0,
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P70}`,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96
    },
    mobileFooterPricing: {
      textAlign: 'center',
      marginBottom: theme.spacing(1.5)
    },
    mobileButton: {
      width: '100%'
    }
  }),
  { name: 'OperationsPromptDialog' }
)

export interface OperationsPromptDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  isPromptPage?: boolean
  onConfirm?: () => any
  onCancel?: () => any
}

export default function OperationsPromptDialog(props: OperationsPromptDialogProps) {
  const classes = useStyles({ ...props })
  const { onCancel, isPromptPage } = props
  const { dismissDialog } = useDialogs()
  const mobile = useMediaQuery(600)

  const handleCancel = () => {
    dismissDialog()
    onCancel?.()
  }

  return (
    <Dialog
      {...props}
      classes={{
        root: classes.dialogRoot,
        paper: classNames({ [classes.dialogPaper]: true, [classes.promptPagePaper]: isPromptPage })
      }}
      fullScreen={mobile}
      maxWidth="sm"
      fullWidth
    >
      <OperationsPrompt {...props} handleCancel={handleCancel} />
    </Dialog>
  )
}

export function OperationsPrompt(props: OperationsPromptDialogProps & { handleCancel: () => void }) {
  const { isSubscribed, isTrial, trialExpired, user } = useUser()
  const { permissionToUpdateBilling } = useUserPermissions()
  const classes = useStyles({ ...props })
  const { onConfirm, isPromptPage, handleCancel } = props
  const [isLoading, setIsLoading] = useState(false)
  const [content, setContent] = useState<'main' | 'price-breakdown'>('main')
  const [desktopImgLoaded, setDesktopImgLoaded] = useState(false)
  const [mobileImgLoaded, setMobileImgLoaded] = useState(!MOBILE_BACKGROUND_IMAGE_URL)
  const { options, clientId: companyId, optionsSync, rates } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const { dismissAllDialogs } = useDialogs()
  const history = useHistory()
  const mobile = useMediaQuery(600)

  const imgLoaded = mobile ? mobileImgLoaded : desktopImgLoaded

  const { data: subscriptionData } = useSubscriptionSummaryQuery({
    fetchPolicy: 'network-only',
    variables: { clientBillingId: user?.app_metadata?.company?.billingId }
  })
  const subscription = subscriptionData?.subscriptionSummary ?? user?.app_metadata?.company?.subscription

  const crmTrialExpiration = options.options?.crm?.trialExpiration
  const showSignupVariant =
    (!!crmTrialExpiration && moment(crmTrialExpiration).isBefore(moment())) ||
    !isSubscribed ||
    (isTrial && trialExpired)

  const isAnnual = subscription?.billing_period_unit === 'year'
  const productPrice = getChargebeeProductPrice('operations')
  const price = isAnnual ? productPrice.yearly / 12 : productPrice.monthly

  const [turnOnCRM] = useMutation(gql`
    mutation TurnOnCRM($trial: Boolean!) {
      turnOnCRM(trial: $trial) {
        success
        trialExpiration
        crmAlreadyActive
        error
      }
    }
  `)

  const handleStartTrial = async () => {
    setIsLoading(true)
    try {
      const { data } = await turnOnCRM({ variables: { trial: true } })
      if (!data.turnOnCRM.success) {
        Sentry.captureException(data.turnOnCRM.error, { tags: { companyId } })
        enqueueSnackbar('Failed to start Operations trial. Please try again later.', { variant: 'error' })
        setIsLoading(false)
        return
      }
      enqueueSnackbar('Operations trial started successfully!', { variant: 'success' })
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('subscription-updates')
        channel.postMessage('addon-modified')
        channel.postMessage('crm-trial-started')
      }
      await optionsSync({ optionsRev: options._rev, ratesRev: rates?._rev })
      track('OperationsTrialStarted')
      if (onConfirm) {
        onConfirm()
      }
      dismissAllDialogs()
      triggerFlow('85160fab-9bea-4df6-8220-8bc23b77b835')
    } catch (error) {
      Sentry.captureException(error, { tags: { companyId } })
      enqueueSnackbar('Failed to start Operations trial. Please try again later.', { variant: 'error' })
      setIsLoading(false)
    }
  }

  const marketingPoints = [
    'Manage customers and jobs in one place',
    'Stay organized from lead to completion',
    'Schedule jobs and keep your team on track'
  ]

  const MainContent = () => {
    const handleNext = () => setContent('price-breakdown')
    const handleSignup = () => history.push('/settings/billing?section=billing-plan')
    const handleLearnMore = () =>
      window.open(
        'https://help.paintscout.com/support/operations/crm/paintscout-operations-guide',
        '_blank',
        'noopener,noreferrer'
      )

    const bottomMessage = !permissionToUpdateBilling
      ? showSignupVariant
        ? 'To continue access, please contact a billing owner on your team.'
        : 'To start your trial, please contact a billing owner on your team.'
      : null

    if (!imgLoaded) {
      return (
        <>
          <Skeleton variant="rect" width="100%" height={320} animation="wave" />
          <DialogActions
            leftButton={
              <Button variant="text" disabled>
                Learn More
              </Button>
            }
          >
            <Button disabled>{showSignupVariant ? 'Sign Up' : 'Start Trial'}</Button>
          </DialogActions>
        </>
      )
    }

    const imageContent = (
      <DialogContent classes={{ root: classes.dialogContent }}>
        {!isPromptPage && (
          <IconButton size="small" onClick={handleCancel} className={classes.closeIcon}>
            <ClearIcon />
          </IconButton>
        )}
        <Typography variant="h2" weight={600} className={classes.title}>
          {showSignupVariant ? 'Get' : 'Try'} Operations <span className={classes.primaryColor}>Today</span>
        </Typography>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Run your entire painting business in one place.
        </Typography>
        <div className={classes.mainContent}>
          <List>
            {marketingPoints.map((text, i) => (
              <ListItem key={i} disableGutters className={classes.listItem}>
                <ListItemIcon className={classes.listItemIcon}>
                  <CheckIcon color="primary" className={classes.checkIcon} />
                </ListItemIcon>
                <ListItemText primary={text} className={classes.listItemText} />
              </ListItem>
            ))}
          </List>
        </div>
        {!mobile && (
          <div className={classes.pricing}>
            {showSignupVariant ? (
              <>
                <Typography className={classes.pricingLine1}>
                  Only <span className={classes.primaryColor}>+${price}/mo</span>
                </Typography>
                <Typography className={classes.pricingLine2}>for your entire team</Typography>
              </>
            ) : (
              <>
                <Typography className={classes.pricingLine1}>
                  30-day <span className={classes.primaryColor}>free trial</span>
                </Typography>
                <Typography className={classes.pricingLine2}>then +${price}/mo for your entire team</Typography>
              </>
            )}
          </div>
        )}
      </DialogContent>
    )

    if (mobile) {
      const trialEndDate = moment().add(30, 'days').format('MMMM Do, YYYY')

      return (
        <div className={classes.mobileWrapper}>
          <div className={classes.mobileImageWrapper}>{imageContent}</div>
          <div className={classes.mobileFooter}>
            {showSignupVariant ? (
              <Typography className={classes.mobileFooterPricing}>
                <strong>Only +${price}/mo for your entire team</strong>
              </Typography>
            ) : (
              <>
                {!bottomMessage && (
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 16 }}>
                    When your trial ends on <strong>{trialEndDate}</strong>, the Operations add-on will be added to your
                    account at a prorated amount for the remainder of your current billing period. All changes are
                    subject to{' '}
                    <Link
                      href="https://www.paintscout.com/terms-of-service/"
                      target="_blank"
                      rel="noopener noreferrer"
                      component={'a' as any}
                    >
                      subscriber terms
                    </Link>
                    .
                  </Typography>
                )}
                <Typography align="center" style={{ marginBottom: 16 }}>
                  <strong>30-day free trial</strong>, then +${price}/mo for your entire team
                </Typography>
                {bottomMessage && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="center"
                    style={{ display: 'block', marginBottom: 12 }}
                  >
                    {bottomMessage}
                  </Typography>
                )}
              </>
            )}
            {permissionToUpdateBilling ? (
              <Button
                className={classes.mobileButton}
                onClick={showSignupVariant ? handleSignup : handleStartTrial}
                disabled={isLoading}
                loading={isLoading}
              >
                {showSignupVariant ? 'Sign Up' : 'Start Trial'}
              </Button>
            ) : (
              <Button className={classes.mobileButton} onClick={handleLearnMore}>
                Learn More
              </Button>
            )}
          </div>
        </div>
      )
    }

    return (
      <>
        {imageContent}
        <DialogActions
          leftButton={
            permissionToUpdateBilling ? (
              <Button variant="text" onClick={handleLearnMore}>
                Learn More
              </Button>
            ) : null
          }
        >
          {bottomMessage && (
            <div className={classes.bottomMessage}>
              <Typography variant="caption" color="textSecondary">
                {bottomMessage}
              </Typography>
            </div>
          )}
          {permissionToUpdateBilling && (
            <Button onClick={showSignupVariant ? handleSignup : handleNext} disabled={isLoading}>
              {showSignupVariant ? 'Sign Up' : 'Start Trial'}
            </Button>
          )}
          {!permissionToUpdateBilling && <Button onClick={handleLearnMore}>Learn More</Button>}
        </DialogActions>
      </>
    )
  }

  if (content === 'main') {
    return (
      <>
        <img src={BACKGROUND_IMAGE_URL} alt="" style={{ display: 'none' }} onLoad={() => setDesktopImgLoaded(true)} />
        {MOBILE_BACKGROUND_IMAGE_URL && (
          <img
            src={MOBILE_BACKGROUND_IMAGE_URL}
            alt=""
            style={{ display: 'none' }}
            onLoad={() => setMobileImgLoaded(true)}
          />
        )}
        <MainContent />
      </>
    )
  }

  const trialEndDate = moment().add(30, 'days').format('MMMM Do, YYYY')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          !isPromptPage ? (
            <IconButton size="small" style={{ padding: 0 }} onClick={handleCancel}>
              <ClearIcon />
            </IconButton>
          ) : null
        }
      >
        Price Breakdown
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContentBreakdown }}>
        <Typography>
          When your trial ends on <strong>{trialEndDate}</strong>, the Operations add-on will be added to your account
          at a prorated amount for the remainder of your current billing period.
        </Typography>
        <Typography style={{ marginTop: 16 }}>
          All changes are subject to{' '}
          <Link
            href="https://www.paintscout.com/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
            component={'a' as any}
          >
            subscriber terms
          </Link>
          .
        </Typography>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="textNoPadding" onClick={() => setContent('main')}>
            Back
          </Button>
        }
      >
        <Button onClick={handleStartTrial} disabled={isLoading} loading={isLoading}>
          Confirm
        </Button>
      </DialogActions>
    </div>
  )
}
